/**
 * Monitor FGC (migrada do Streamlit; equivalência em backend/tests_paginas/test_fgc.py).
 *
 * Só instrumentos cobertos pelo FGC: exposição a emissores não aprovados e excesso
 * sobre o teto por emissor. Na URL: carteiras (vazio = todas, como o legado) e "apenas
 * clientes com alerta". Carteiras filtradas por `portfolios_visiveis` no servidor.
 */
import { Alert, Checkbox, Loader, MultiSelect, SimpleGrid, Text } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import { BarraFiltros, GrupoFiltros, LARGURA } from '../../components/Filtros';
import { Kpi, type KpiApi } from '../../components/Kpi';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { useFiltrosUrl } from '../../lib/url-state';

const CAMINHO = '/carteiras-administradas/monitor-fgc';

interface Dados {
  opcoes: { carteiras: string[] };
  info?: string;
  kpis?: KpiApi[];
  resumo?: TabelaApi | null;
  por_emissor?: TabelaApi;
  detalhe?: TabelaApi | null;
  limite?: string;
}

const lista = (t: string) => (t ? t.split(',') : []);

function Vazio({ texto }: { texto: string }) {
  return (
    <Text size="sm" c="dimmed">
      {texto}
    </Text>
  );
}

export function MonitorFgc() {
  const [filtros, definir] = useFiltrosUrl({ carteiras: '', so_alertas: false as boolean });
  const parametros = { carteiras: lista(filtros.carteiras), so_alertas: filtros.so_alertas };
  const consulta = useQuery({
    queryKey: ['carteiras.fgc', parametros],
    queryFn: ({ signal }) => apiGet<Envelope<Dados>>(CAMINHO, parametros, signal),
    placeholderData: keepPreviousData,
  });
  const dados = consulta.data?.data;

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Monitor FGC"
        descricao="Ativos cobertos pelo FGC nas carteiras: exposição a emissores não aprovados e excesso sobre o teto por emissor."
        dadosAte={consulta.data?.meta.dados_ate}
        fonte="Fibery (Posição Consolidada, Ativos, Emissores, Classificação Instrumento)"
      />
      <BarraFiltros carregando={consulta.isFetching}>
        <GrupoFiltros titulo="Filtros">
          <MultiSelect
            label="Carteiras"
            placeholder={parametros.carteiras.length ? '' : 'Todas'}
            data={dados?.opcoes.carteiras ?? []}
            value={parametros.carteiras}
            onChange={(v) => definir({ carteiras: v.join(',') })}
            searchable
            clearable
            w={LARGURA.selecao + 80}
          />
          <Checkbox
            label="Apenas clientes com alerta"
            description="Saldo não aprovado ou acima do limite"
            checked={filtros.so_alertas}
            onChange={(e) => definir({ so_alertas: e.currentTarget.checked })}
            mt={24}
          />
        </GrupoFiltros>
      </BarraFiltros>
      <AvisosApi avisos={[consulta.data?.meta.avisos]} />
      {consulta.isError && (
        <Alert color="red">
          {consulta.error instanceof ErroApi
            ? consulta.error.message
            : 'Não foi possível carregar as posições.'}
        </Alert>
      )}
      {consulta.isPending && <Loader />}
      {dados?.info && <Alert color="blue">{dados.info}</Alert>}
      {dados?.kpis && dados.por_emissor && (
        <>
          <SimpleGrid cols={{ base: 2, lg: 4 }}>
            {dados.kpis.map((k) => (
              <Kpi key={k.rotulo} kpi={k} />
            ))}
          </SimpleGrid>
          <Secao
            titulo="Resumo por cliente"
            legenda="Só instrumentos cobertos pelo FGC: exposição a emissores não aprovados e estouro do teto por emissor. Linhas destacadas têm alerta."
          >
            {dados.resumo ? (
              <TabelaDados tabela={dados.resumo} alturaAutomatica />
            ) : (
              <Vazio texto="Nenhum cliente com alerta para os filtros selecionados." />
            )}
          </Secao>
          <Secao
            titulo="Cobertura FGC por emissor"
            legenda={`Saldo agregado por emissor (limite de ${dados.limite} por titular). Linhas destacadas passam do limite.`}
          >
            <TabelaDados tabela={dados.por_emissor} altura={420} />
          </Secao>
          <Secao titulo="Detalhamento · ativos não aprovados (FGC)">
            {dados.detalhe ? (
              <TabelaDados tabela={dados.detalhe} altura={420} />
            ) : (
              <Vazio texto="Nenhum ativo FGC de emissor não aprovado encontrado." />
            )}
          </Secao>
        </>
      )}
    </Pagina>
  );
}
